import axios from "axios";
import tokenManagerService from "./tokenManager";

/**
 * Lazy load the Vuex store to avoid circular dependency
 */
let vuexStore = null;
const getVuexStore = () => {
  if (!vuexStore) {
    try {
        vuexStore = require('../store').default;
    } catch (e) { 
        console.warn("Could not lazy load Vuex store", e); 
    }
  }
  return vuexStore;
};

class BaseHttpService {
  constructor(baseURL) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 100000,
      headers: { "Content-Type": "application/json" },
    });

    this._tokenCache = {
      token: null,
      lastFetch: null,
      cacheDuration: 2 * 60 * 1000,
    };

    this.initializeInterceptors();
  }

  clearTokenCache() {
    this._tokenCache.token = null;
    this._tokenCache.lastFetch = null;
  }

  async getAccessToken() {
    // ... (Your existing caching logic is fine) ...
    try {
      const now = Date.now();
      if (this._tokenCache.token && this._tokenCache.lastFetch && 
          (now - this._tokenCache.lastFetch) < this._tokenCache.cacheDuration) {
        return this._tokenCache.token;
      }

      const validAccessToken = await tokenManagerService.getValidToken();
      
      if (validAccessToken) {
        const token = { accessToken: validAccessToken };
        this._tokenCache.token = token;
        this._tokenCache.lastFetch = now;
        return token;
      }
      return null;
    } catch (error) {
      console.error('❌ Error getting access token:', error);
      return null;
    }
  }

  initializeInterceptors() {
    // --- REQUEST INTERCEPTOR ---
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          const tokenResult = await this.getAccessToken();
          
          // FIX 1: Add 'Bearer ' prefix
          if (tokenResult && tokenResult.accessToken) {
            config.headers.authorization = `Bearer ${tokenResult.accessToken}`;
          } else {
             console.warn("⚠️ Sending request without access token (Auth failed or Public EP)");
          }
          
          // Get activeApp from Vuex store
          const store = getVuexStore();
          const aid = store?.getters?.['app/activeApp'];
      
          if (!config.params) config.params = {};
          // Caller-provided aid wins over the activeApp default — needed by
          // app-agnostic pages (e.g. /admin) that pick an app per call.
          if (!config.params.aid && aid) config.params.aid = aid;

          // Logging (Keep strictly for dev, remove in prod)
          // console.log("Outgoing Request:", config.url);
        } catch (error) {
          console.error("Interceptor Setup Error:", error);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // --- RESPONSE INTERCEPTOR ---
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        // Handle 401 Unauthorized
        if (status === 401) {
          console.error('🔒 401 Unauthorized:', error.config.url);
          return Promise.reject(error);
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * Sends a GET request.
   * @param {string} url - The endpoint URL relative to the base URL.
   * @param {object} [params={}] - Query parameters to include in the request.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  get(url, params = {}) {
    return this.axiosInstance.get(url, { params });
  }

  /**
   * Sends a POST request.
   * @param {string} url - The endpoint URL relative to the base URL.
   * @param {object} data - The payload to send in the request body.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  post(url, data) {
    return this.axiosInstance.post(url, data);
  }

  /**
   * Sends a PUT request.
   * @param {string} url - The endpoint URL relative to the base URL.
   * @param {object} data - The payload to send in the request body.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  put(url, data) {
    return this.axiosInstance.put(url, data);
  }

  /**
   * Sends a DELETE request.
   * @param {string} url - The endpoint URL relative to the base URL.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  delete(url) {
    return this.axiosInstance.delete(url);
  }
}

export default BaseHttpService;
